<template>
  <div class="version-admin">
    <el-card v-if="can('上传')" shadow="hover" class="publish-card">
      <template #header>
        <div class="card-header">
          <span>发布新版本</span>
        </div>
      </template>

      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="120px"
        label-position="right"
      >
        <el-form-item label="平台类型" prop="type">
          <el-radio-group v-model="form.type">
            <el-radio value="安卓">安卓</el-radio>
            <el-radio value="鸿蒙">鸿蒙</el-radio>
            <el-radio value="windows">Windows</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="!isWindows" :label="versionLabel" prop="version">
          <el-input
            v-model="form.version"
            :placeholder="versionPlaceholder"
            :disabled="parsing"
            style="max-width: 260px"
          >
            <template #suffix>
              <el-icon v-if="parsing" class="is-loading"><Loading /></el-icon>
            </template>
          </el-input>
          <span v-if="isAndroid && parsedInfo" class="form-tip" style="color: #67c23a">
            已从 APK 解析: versionName={{ parsedInfo.versionName }}, versionCode={{ parsedInfo.versionCode }}
          </span>
          <span v-else-if="isHarmony && parsedHapInfo" class="form-tip" style="color: #67c23a">
            已从 {{ parsedHapInfo.source }} 解析: versionCode={{ parsedHapInfo.versionCode }}
          </span>
          <span v-else-if="(isAndroid || isHarmony) && parseError" class="form-tip" style="color: #f56c6c">
            {{ parseError }}（请手动输入版本号）
          </span>
          <span v-else class="form-tip">{{ versionTip }}</span>
        </el-form-item>

        <el-form-item label="说明" prop="remarks">
          <el-input
            v-model="form.remarks"
            type="textarea"
            :rows="4"
            placeholder="1. 修复xx问题&#10;2. 新增xx功能"
            style="max-width: 500px"
          />
        </el-form-item>

        <el-form-item label="安装包文件">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :accept="uploadAccept"
            drag
            style="max-width: 500px"
          >
            <el-icon style="font-size: 40px; color: #909399; margin-bottom: 8px">
              <UploadFilled />
            </el-icon>
            <div>将安装包拖拽到此处，或 <em>点击选择文件</em></div>
            <template #tip>
              <div class="el-upload__tip">
                {{ acceptTip }}，单个文件不超过 200MB
              </div>
            </template>
          </el-upload>
        </el-form-item>

        <el-form-item v-if="uploading" label="上传进度">
          <el-progress
            :percentage="uploadProgress"
            :status="uploadProgress >= 100 ? 'success' : ''"
            style="max-width: 500px"
          />
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            size="large"
            :loading="uploading"
            @click="handlePublish"
          >
            {{ uploading ? '上传中...' : '发布版本' }}
          </el-button>
          <el-button size="large" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="hover" class="list-card">
      <template #header>
        <div class="card-header">
          <span>已发布版本</span>
          <el-button type="primary" text @click="loadVersionList">刷新</el-button>
        </div>
      </template>

      <el-table :data="versionList" stripe style="width: 100%" v-loading="listLoading">
        <el-table-column prop="版本号" label="版本号" width="120" />
        <el-table-column prop="类型" label="平台" width="100">
          <template #default="{ row }">
            <el-tag :type="platformTagType(row['类型'])">
              {{ row['类型'] }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="文件名" label="文件名" width="180" />
        <el-table-column prop="说明" label="说明" min-width="200" show-overflow-tooltip />
        <el-table-column prop="上传人" label="上传人" width="120" />
        <el-table-column prop="上传日期" label="上传时间" width="180" />
        <el-table-column v-if="can('下载')" label="操作" width="140" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link size="small" @click="handleCopyUrl(row)">
              复制下载链接
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { UploadFilled, Loading } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import AppVersionAdminApi from '@/network/apis/AppVersionAdmin'
import { parseApkVersion } from '@/utils/apkParser'
import type { ApkVersionInfo } from '@/utils/apkParser'
import { parseHapVersion } from '@/utils/hapParser'
import type { HapVersionInfo } from '@/utils/hapParser'
import { useButtonPermission } from '@/utils/buttonPermission'

const { can } = useButtonPermission('/KaiFa/AppVersionAdmin/index.vue')

const formRef = ref()
const uploadRef = ref()
const form = reactive({
  type: '安卓',
  version: '',
  remarks: '',
  file: null as File | null
})

const isWindows = computed(() => form.type === 'windows')
const isAndroid = computed(() => form.type === '安卓')
const isHarmony = computed(() => form.type === '鸿蒙')

const rules = {
  type: [{ required: true, message: '请选择平台', trigger: 'change' }],
  version: [
    {
      validator: (_rule: any, value: string, callback: (error?: Error) => void) => {
        if (isWindows.value) {
          callback()
          return
        }
        if (!value) {
          callback(new Error('请输入版本号'))
          return
        }
        if (!/^\d+(\.\d+)*$/.test(value)) {
          callback(new Error('格式如 1.4 或 1.3.34'))
          return
        }
        callback()
      },
      trigger: 'blur'
    }
  ],
  remarks: [{ required: true, message: '请输入更新说明', trigger: 'blur' }]
}

const uploadAccept = computed(() => {
  if (form.type === '安卓') return '.apk'
  if (form.type === 'windows') return '.exe,.msi,.zip'
  return '.hap'
})

const acceptTip = computed(() => {
  if (form.type === '安卓') return '支持 .apk 文件'
  if (form.type === 'windows') return '支持 .exe / .msi / .zip 文件'
  return '支持 .hap 文件'
})

const versionLabel = computed(() => isWindows.value ? '版本号（选填）' : '版本号')

const versionPlaceholder = computed(() => {
  if (isAndroid.value) {
    return parsing.value ? '正在解析 APK...' : '如 1.4 或 1.3.34'
  }
  if (isWindows.value) {
    return '可不填，如需填写可输入 1.4 或 1.3.34'
  }
  if (isHarmony.value) {
    return parsing.value ? '正在解析 HAP...' : '上传 HAP 后自动填充 versionCode'
  }
  return '如 1.4 或 1.3.34'
})

const versionTip = computed(() => {
  if (isAndroid.value) return '选择 APK 文件后自动解析版本号'
  if (isWindows.value) return 'Windows 版本上传无需输入版本号'
  if (isHarmony.value) return '选择 HAP 文件后自动解析 versionCode'
  return '请输入版本号'
})

const platformTagType = (type: string) => {
  if (type === '安卓') return 'success'
  if (type === '鸿蒙') return 'warning'
  if (type === 'windows') return '' as const
  return 'info'
}

const uploading = ref(false)
const uploadProgress = ref(0)
const parsing = ref(false)
const parsedInfo = ref<ApkVersionInfo | null>(null)
const parsedHapInfo = ref<HapVersionInfo | null>(null)
const parseError = ref('')

const clearUploadState = () => {
  uploadRef.value?.clearFiles()
  form.file = null
  parsedInfo.value = null
  parsedHapInfo.value = null
  parseError.value = ''
  parsing.value = false
}

watch(() => form.type, () => {
  clearUploadState()
  form.version = ''
  loadVersionList()
})

const isApkFile = (file: File) => file.name.toLowerCase().endsWith('.apk')
const isHapFile = (file: File) => file.name.toLowerCase().endsWith('.hap')

const handleFileChange = async (uploadFile: any) => {
  const rawFile = uploadFile.raw as File
  if (!rawFile) {
    return
  }

  if (isAndroid.value && !isApkFile(rawFile)) {
    ElMessage.warning('安卓版本仅支持上传 .apk 格式文件')
    clearUploadState()
    return
  }

  if (isHarmony.value && !isHapFile(rawFile)) {
    ElMessage.warning('鸿蒙版本仅支持上传 .hap 格式文件')
    clearUploadState()
    return
  }

  form.file = uploadFile.raw
  parsedInfo.value = null
  parsedHapInfo.value = null
  parseError.value = ''

  if (form.file && form.file.name.toLowerCase().endsWith('.apk')) {
    parsing.value = true
    try {
      const info = await parseApkVersion(form.file)
      parsedInfo.value = info
      if (info.versionName) {
        form.version = info.versionName
      }
    } catch (err) {
      console.error('APK 解析失败:', err)
      parseError.value = 'APK 版本解析失败'
    } finally {
      parsing.value = false
    }
    return
  }

  if (form.file && isHarmony.value && isHapFile(form.file)) {
    parsing.value = true
    try {
      const info = await parseHapVersion(form.file)
      parsedHapInfo.value = info
      form.version = info.versionCode
    } catch (err) {
      console.error('HAP 解析失败:', err)
      parseError.value = 'HAP 版本解析失败'
    } finally {
      parsing.value = false
    }
  }
}

const handleFileRemove = () => {
  form.file = null
  parsedInfo.value = null
  parsedHapInfo.value = null
  parseError.value = ''
}

const handlePublish = async () => {
  try {
    await formRef.value.validate()
  } catch {
    return
  }

  if (!form.file) {
    ElMessage.warning('请选择安装包文件')
    return
  }

  if (isAndroid.value && !isApkFile(form.file)) {
    ElMessage.warning('安卓版本仅支持上传 .apk 格式文件')
    return
  }

  if (isHarmony.value && !isHapFile(form.file)) {
    ElMessage.warning('鸿蒙版本仅支持上传 .hap 格式文件')
    return
  }

  uploading.value = true
  uploadProgress.value = 0

  try {
    const res = await AppVersionAdminApi.uploadPackage(
      form.file,
      { version: form.version, type: form.type, remarks: form.remarks },
      (percent: number) => { uploadProgress.value = percent }
    )
    if (res && res.status === 200) {
      ElMessage.success(res.msg || '上传成功')
    } else {
      ElMessage.success('上传成功')
    }
    handleReset()
    loadVersionList()
  } catch (err: any) {
    const msg = err?.response?.data?.msg || err?.response?.data?.message || '上传失败，请检查网络或后端服务'
    ElMessage.error(msg)
  } finally {
    uploading.value = false
  }
}

const handleReset = () => {
  const currentType = form.type
  formRef.value?.resetFields()
  form.type = currentType
  clearUploadState()
  uploadProgress.value = 0
}

const versionList = ref<any[]>([])
const listLoading = ref(false)

const buildVersionListFilters = () => ([
  {
    FieldName: '类型',
    FiedType: 'string',
    Type: 3,
    Value1: form.type,
    Value2: ''
  }
])

const loadVersionList = async () => {
  listLoading.value = true
  try {
    const res: any = await AppVersionAdminApi.getVersionList({}, buildVersionListFilters())
    const respData = res?.response || res
    const list = respData?.data || respData || []
    versionList.value = Array.isArray(list) ? list : []
  } catch (err) {
    console.error('获取版本列表失败:', err)
  } finally {
    listLoading.value = false
  }
}

const handleCopyUrl = (row: any) => {
  const fileName = row['文件名']
  if (!fileName) {
    ElMessage.warning('无文件名')
    return
  }
  const url = AppVersionAdminApi.getDownloadUrl(fileName)
  navigator.clipboard.writeText(url).then(() => {
    ElMessage.success('下载链接已复制到剪贴板')
  }).catch(() => {
    const input = document.createElement('input')
    input.value = url
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    ElMessage.success('下载链接已复制到剪贴板')
  })
}

onMounted(() => {
  loadVersionList()
})
</script>

<style lang="less" scoped>
.version-admin {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
}

.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}

.publish-card {
  :deep(.el-upload-dragger) {
    padding: 30px 20px;
  }
}
</style>
